import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Upload, FileSpreadsheet, CheckCircle2, Loader2, ChevronDown, ChevronUp, Info,
    RefreshCw, Clock,
} from 'lucide-react';
import { getImportRunsApi, getImportRunApi, IMPORTS_PER_PAGE } from '../../services/importApi';
import { getStaffListApi } from '../../services/staffApi';
import { SLOTS } from './importSlots';
import ImportSlot from './ImportSlot';
import {
    prepareSlotFile, splitSlotFile, sortSlotFiles, sameFile, MAX_UPLOAD_BYTES, MAX_ROWS_PER_UPLOAD,
} from './slotFile';
import { uploadBatch } from './batchUpload';
import { runDuration } from './runDuration';

/**
 * Importing a workshop's history out of Machanicdesk.
 *
 * Nine slots, one press. Each export is dropped on the slot with its name, the file's
 * own header is checked against that slot on the spot, an oversized one is split
 * without being asked, and Start sends the lot in slot order and hands them to the
 * server. The server runs them one after another — about half an hour for a whole
 * workshop — and the log below is where that progress shows.
 *
 * ⚠️ There is no preview: uploading imports. What makes that safe is that re-running is
 * harmless — every path keys on the source system's own id, so a second run matches what
 * the first created instead of duplicating it. The report is what you read afterwards:
 * it names every skipped row, and every value a column could not hold.
 */

// Re-exported so the tests that pin the caps keep one import.
export { MAX_UPLOAD_BYTES, MAX_ROWS_PER_UPLOAD };

/**
 * How often the log asks again while something is queued or running.
 *
 * 🔑 Ten seconds against a run that takes minutes per file. The import runs on the
 * SERVER, not in this tab; without a way to see it move, the only thing a person can
 * do while waiting is reload — and a reload on a page that looks stuck is how one
 * import became two in production.
 */
export const POLL_MS = 10_000;

/** A run the server has not finished with yet — the log keeps asking while one exists. */
export const isUnfinished = (run) => run.status === 'QUEUED' || run.status === 'RUNNING';

export default function ImportsTab() {
    const [runs, setRuns] = useState([]);
    const [page, setPage] = useState(0);
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    // How many people this workshop has. Only ever used to warn — see the banner below.
    const [staffCount, setStaffCount] = useState(null);

    /** What is in each slot, keyed by export kind. */
    const [slotFiles, setSlotFiles] = useState({});
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(null);
    const [started, setStarted] = useState(null);
    const [expanded, setExpanded] = useState(null);
    const [detail, setDetail] = useState(null);

    const load = useCallback(async (which = page) => {
        try {
            const data = await getImportRunsApi(which);
            // ⚠️ Tolerate the old unpaged shape: a browser holding the previous bundle
            // against a new server, or the reverse during a deploy, would otherwise
            // white-screen on data.items.map.
            const items = Array.isArray(data) ? data : (data?.items ?? []);
            setRuns(items);
            setTotal(Array.isArray(data) ? items.length : (data?.total ?? items.length));
            setTotalPages(Array.isArray(data) ? 1 : (data?.totalPages ?? 1));
            // A page that no longer exists — the last run on it was the last run there
            // was — would otherwise show an empty list with no way back.
            if (which > 0 && items.length === 0) {
                setPage(which - 1);
            }
        } catch {
            setError('Could not load previous imports.');
        } finally {
            setLoading(false);
        }
    }, [page]);

    useEffect(() => { load(page); }, [load, page]);

    // 🔑 The live half of the log. While anything on this page is queued or running,
    // ask again every POLL_MS; the moment nothing is, stop — a tab left open overnight
    // must not poll an idle server all night.
    const busy = runs.some(isUnfinished);
    useEffect(() => {
        if (!busy) {
            return undefined;
        }
        const timer = setInterval(() => { load(page); }, POLL_MS);
        return () => clearInterval(timer);
    }, [busy, load, page]);

    // 🔑 Asked once, and a failure is silent. Whoever can reach this tab may not have
    // staff:read, and a missing WARNING must never become a visible error on a screen
    // whose actual job is importing.
    useEffect(() => {
        getStaffListApi({ page: 0, size: 1 })
            .then((d) => setStaffCount(d?.totalElements ?? null))
            .catch(() => setStaffCount(null));
    }, []);

    /** Replace one file's entry in its slot, keeping the slot in upload order. */
    const patchFile = (kind, id, patch) => setSlotFiles((prev) => ({
        ...prev,
        [kind]: {
            files: sortSlotFiles((prev[kind]?.files ?? []).map((f) => (f.id === id ? { ...f, ...patch } : f))),
        },
    }));

    /**
     * ADD files to a slot — never replace. The exports arrive pre-split, and dropping
     * part 3 after parts 1 and 2 must make three, not one.
     */
    const dropFiles = async (kind, files) => {
        setError(null);
        setStarted(null);
        const existing = slotFiles[kind]?.files ?? [];
        let added = existing.reduce((n, f) => Math.max(n, f.added + 1), 0);
        const fresh = files.filter((file) => !existing.some((f) => sameFile(f.file, file)));
        // Placeholders first, so every file shows at once while each is read in turn.
        const placeholders = fresh.map((file, i) => ({
            id: `${file.name}:${file.size}:${added + i}`, file, rows: null, parts: [],
            needsSplit: false, split: false, part: null, added: added + i, preparing: true,
        }));
        setSlotFiles((prev) => ({
            ...prev,
            [kind]: { files: sortSlotFiles([...(prev[kind]?.files ?? []), ...placeholders]) },
        }));
        for (const placeholder of placeholders) {
            try {
                const ready = await prepareSlotFile(placeholder.file, kind, placeholder.added);
                patchFile(kind, placeholder.id, { ...ready, preparing: false, error: null });
            } catch (err) {
                // Refused ALONE: the others in the slot stand. Kept in the list with its
                // reason so the person can see which of three near-identical files it was.
                patchFile(kind, placeholder.id, {
                    preparing: false, error: err?.message || 'This file could not be read.',
                });
            }
            added += 1;
        }
    };

    const removeFile = (kind, id) => setSlotFiles((prev) => {
        const files = (prev[kind]?.files ?? []).filter((f) => f.id !== id);
        const next = { ...prev };
        if (files.length) {
            next[kind] = { files };
        } else {
            delete next[kind];
        }
        return next;
    });

    /** The visible split: every over-cap file in the slot, now. */
    const splitSlot = async (kind) => {
        const targets = (slotFiles[kind]?.files ?? []).filter((f) => f.needsSplit && !f.split && !f.error);
        for (const entry of targets) {
            patchFile(kind, entry.id, { splitting: true });
            try {
                patchFile(kind, entry.id, { ...(await splitSlotFile(entry)), splitting: false });
            } catch (err) {
                patchFile(kind, entry.id, { splitting: false, error: err?.message || 'This file could not be split.' });
            }
        }
    };

    // In SLOT order, then the slot's own order — whatever order the files were dropped
    // in. That is the whole point.
    const filled = useMemo(() => SLOTS
        .map((s) => ({ kind: s.kind, files: (slotFiles[s.kind]?.files ?? []).filter((f) => !f.error && !f.preparing) }))
        .filter((s) => s.files.length > 0), [slotFiles]);
    const blocked = Object.values(slotFiles).some((s) => s.files.some((f) => f.error || f.preparing || f.splitting));
    const canStart = filled.length > 0 && !blocked && !uploading && !busy;

    const handleStart = async () => {
        if (!canStart) return;
        setUploading(true);
        setError(null);
        setStarted(null);
        try {
            // The fallback split: anything still over the caps is cut here, in place,
            // so the visible button is a choice and not a requirement.
            const batch = [];
            for (const slot of filled) {
                const parts = [];
                for (const entry of slot.files) {
                    parts.push(...(await splitSlotFile(entry)).parts);
                }
                batch.push({ kind: slot.kind, parts });
            }
            const result = await uploadBatch(batch, {
                onProgress: (done, count) => setUploadProgress({ done, total: count }),
            });
            setStarted(result);
            setSlotFiles({});
            // 🔑 Back to the first page: the runs just queued are the newest, and
            // leaving the reader on page 4 hides the very thing they are waiting for.
            setPage(0);
            await load(0);
        } catch (err) {
            // Verbatim: the server names the file it refused and why. What was queued
            // before the failure stays queued and is NOT started — see uploadBatch.
            setError(err?.response?.data?.message || err?.message || 'The upload failed.');
        } finally {
            setUploading(false);
            setUploadProgress(null);
        }
    };

    const openRun = async (uuid) => {
        if (expanded === uuid) { setExpanded(null); return; }
        setExpanded(uuid);
        setDetail(null);
        try {
            setDetail(await getImportRunApi(uuid));
        } catch {
            setError('Could not load that import.');
        }
    };

    return (
        <div className="space-y-6">
            {/* ── Upload ─────────────────────────────────────────────────── */}
            <div className="bg-white rounded-2xl border border-gray-200 p-5 space-y-4">
                <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-cyan-50 flex items-center justify-center shrink-0">
                        <FileSpreadsheet size={20} className="text-cyan-600" />
                    </div>
                    <div>
                        <h3 className="font-bold text-gray-900">Import from Machanicdesk</h3>
                        <p className="text-sm text-gray-500">
                            Brings across customers, their vehicles, invoices, payments and
                            quotes. Jobs come across as service history, each carrying its
                            invoice lines as work items — nothing lands on the board. Once
                            the files are in, invoices still owed and quotes the customer never
                            answered are raised as jobs automatically. Export each list from
                            Machanicdesk as CSV and drop it on its slot.
                        </p>
                    </div>
                </div>

                {/* Nine slots. Numbered because the number is the order they run in,
                    and it is what the mismatch message points a person at. */}
                <ol className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 gap-1.5">
                    {SLOTS.map((slot, i) => (
                        <ImportSlot
                            key={slot.kind}
                            slot={slot}
                            number={i + 1}
                            state={slotFiles[slot.kind]}
                            disabled={uploading}
                            onFiles={(files) => dropFiles(slot.kind, files)}
                            onRemove={(id) => removeFile(slot.kind, id)}
                            onSplit={() => splitSlot(slot.kind)}
                        />
                    ))}
                </ol>
                <p className="text-xs text-gray-500">
                    Each file says which export it is, so one dropped on the wrong slot is
                    refused there and then — there is nothing to choose and nothing to get
                    wrong about <em>which</em> import you are running. A slot takes as many
                    files as the export was split into — drop them all, in any order — and
                    a file too large to send whole is split into parts for you.
                </p>

                {/* 🔴 Always shown, not conditional. The slots run in order, so the order
                    itself can no longer go wrong — but a MISSING file still can. Every
                    import keys on the source system's own id and skips what it has already
                    brought in, so filling the gap later does not repair what was skipped
                    without it. */}
                <div className="rounded-xl border border-amber-300 bg-amber-50 p-3">
                    <p className="text-xs font-bold text-amber-900">
                        The slots run in the order above, one after another — running them
                        again does not fix what a missing file skipped.
                    </p>
                    <p className="text-[11px] text-amber-800 mt-1 leading-snug">
                        Every import skips what it has already brought in, so a second run
                        repairs nothing. Lines need the invoice or quote they belong to;
                        jobs take their dates and work items from the invoices; payments
                        and credit notes need their invoices; everything needs customers.
                        Fill every slot you have before pressing Start.
                        <strong className="font-semibold"> Add your team under Team Members
                        before Jobs and Invoices</strong> — those files name who finished
                        each job and who sold each invoice, and a name with nobody to match
                        is filed under you instead.
                    </p>
                </div>

                {/* 🔴 Before the import, because afterwards is too late. The jobs and
                    invoice exports name the person who finished the work and the person
                    who sold it, and those names are matched to staff. Re-importing does
                    NOT backfill: an already-imported job or invoice is skipped whole, so
                    the link is never made on a second run. */}
                {staffCount !== null && staffCount <= 1 && (
                    <div className="rounded-xl border border-amber-200 bg-amber-50 p-3">
                        <p className="text-xs font-semibold text-amber-900">
                            Add your team before importing jobs or invoices
                        </p>
                        <p className="text-[11px] text-amber-800 mt-1 leading-snug">
                            This workshop has only one person on it. The Jobs export names
                            who finished each job, and the Invoices export names who sold
                            each invoice — those names are matched to your team, and with
                            nobody to match they are kept as plain text and linked to no one.
                            <strong className="font-semibold"> Importing again later does not
                            fix it</strong>, because rows already imported are skipped. Add
                            the team under Team Members first.
                        </p>
                    </div>
                )}

                <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                    <button
                        type="button"
                        onClick={handleStart}
                        disabled={!canStart}
                        className="flex items-center justify-center gap-2 px-6 py-2.5 rounded-xl
                                   bg-gray-900 text-white text-sm font-medium min-h-[44px]
                                   disabled:opacity-40 hover:bg-gray-800 transition-colors"
                    >
                        {uploading ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />}
                        {uploading && uploadProgress
                            ? `Uploading ${uploadProgress.done} of ${uploadProgress.total}…`
                            : 'Start import'}
                    </button>

                    {/* 🔑 The import runs on the SERVER, not in this tab. The log below
                        refreshes itself while something is running; this is for the
                        person who does not trust that, and for after it stops. */}
                    <button
                        type="button"
                        onClick={() => load(page)}
                        disabled={loading}
                        className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-gray-200
                                   text-sm font-medium text-gray-700 min-h-[44px]
                                   hover:bg-gray-50 disabled:opacity-40 transition-colors"
                        title="Refresh the import log"
                    >
                        <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />
                        Refresh
                    </button>
                </div>

                {/* Said where the waiting happens, not buried in the legend above. */}
                <p className="text-xs text-gray-500">
                    Start sends the files up in slot order and the server runs them one
                    after another — a whole workshop takes about half an hour. Closing this
                    page or losing your connection does not stop it, and does not undo it.
                    The log below shows each file as it is queued, running and done.
                </p>

                {busy && !uploading && (
                    <p className="flex items-center gap-2 text-xs text-gray-600">
                        <Loader2 size={13} className="animate-spin" />
                        An import is running for this workshop. Start is available again
                        when it finishes.
                    </p>
                )}

                {error && (
                    <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-xl p-3">
                        {error}
                    </p>
                )}

                {started && (
                    <div className="flex items-start gap-2 rounded-xl border border-emerald-200 bg-emerald-50 p-3">
                        <CheckCircle2 size={15} className="text-emerald-600 mt-0.5 shrink-0" />
                        <p className="text-xs text-emerald-900">
                            {started.uploaded} {started.uploaded === 1 ? 'file' : 'files'} queued
                            and started. Progress appears below as each one runs.
                        </p>
                    </div>
                )}
            </div>

            {/* ── History, and the live log ──────────────────────────────── */}
            <div className="bg-white rounded-2xl border border-gray-200">
                <h3 className="px-5 py-4 font-bold text-gray-900 border-b border-gray-100 flex items-center gap-2">
                    Previous imports
                    {busy && <Loader2 size={14} className="animate-spin text-gray-400" />}
                </h3>
                {loading ? (
                    <p className="px-5 py-6 text-sm text-gray-400">Loading…</p>
                ) : runs.length === 0 ? (
                    <p className="px-5 py-6 text-sm text-gray-400">Nothing imported yet.</p>
                ) : (
                    <div className="divide-y divide-gray-100">
                        {runs.map((run) => (
                            <div key={run.uuid}>
                                <button
                                    type="button"
                                    onClick={() => openRun(run.uuid)}
                                    className="w-full flex items-center justify-between px-5 py-3 text-left hover:bg-gray-50"
                                >
                                    <div className="min-w-0 flex items-start gap-2">
                                        <StatusIcon status={run.status} />
                                        <div className="min-w-0">
                                            <p className="text-sm font-medium text-gray-900 truncate">
                                                {runTitle(run)}
                                                {/* ⚠️ Kept for the runs made while the
                                                    preview existed. There is no preview now,
                                                    so this never shows on a new run — but
                                                    relabelling the old ones would make the
                                                    history lie about what was written. */}
                                                {run.dryRun && (
                                                    <span className="ml-2 text-[11px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500">
                                                        preview
                                                    </span>
                                                )}
                                            </p>
                                            <p className="text-xs text-gray-500">
                                                {summarise(run)}
                                                {runDuration(run) && (
                                                    <span className="text-gray-400"> · {runDuration(run)}</span>
                                                )}
                                                {run.rowsSkipped > 0 && (
                                                    <span className="text-amber-700"> · {run.rowsSkipped} skipped</span>
                                                )}
                                            </p>
                                        </div>
                                    </div>
                                    {expanded === run.uuid
                                        ? <ChevronUp size={16} className="text-gray-400 shrink-0" />
                                        : <ChevronDown size={16} className="text-gray-400 shrink-0" />}
                                </button>
                                {expanded === run.uuid && <RunDetail run={run} detail={detail} />}
                            </div>
                        ))}
                    </div>
                )}
                {totalPages > 1 && (
                    <Pager
                        page={page}
                        size={IMPORTS_PER_PAGE}
                        total={total}
                        totalPages={totalPages}
                        onChange={(next) => { setExpanded(null); setPage(next); }}
                    />
                )}
            </div>
        </div>
    );
}

/** The state of a run at a glance, before the words. */
function StatusIcon({ status }) {
    if (status === 'RUNNING') {
        return <Loader2 size={15} className="animate-spin text-cyan-600 mt-0.5 shrink-0" data-testid="run-spinner" />;
    }
    if (status === 'QUEUED') {
        return <Clock size={15} className="text-gray-400 mt-0.5 shrink-0" />;
    }
    return null;
}

/**
 * Newer / older through the import history.
 *
 * <p>Shows the RANGE and the total, not just a page number: "21–40 of 137" answers both
 * "where am I" and "how much is there", and after a migration the second is the question
 * — a workshop wants to know all ten parts landed.
 *
 * <p>⚠️ Hidden entirely when there is only one page. A pager under a list of three is
 * furniture that has to be read before it can be ignored.
 */
function Pager({ page, size, total, totalPages, onChange }) {
    const first = page * size + 1;
    const last = Math.min((page + 1) * size, total);

    return (
        <div className="flex items-center justify-between gap-3 px-5 py-3 border-t border-gray-100">
            <p className="text-xs text-gray-500">
                {first}–{last} of {total}
            </p>
            <div className="flex items-center gap-2">
                <button
                    type="button"
                    onClick={() => onChange(page - 1)}
                    disabled={page === 0}
                    className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200
                               text-gray-700 hover:bg-gray-50 disabled:opacity-40
                               disabled:cursor-not-allowed"
                >
                    Newer
                </button>
                <button
                    type="button"
                    onClick={() => onChange(page + 1)}
                    disabled={page >= totalPages - 1}
                    className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200
                               text-gray-700 hover:bg-gray-50 disabled:opacity-40
                               disabled:cursor-not-allowed"
                >
                    Older
                </button>
            </div>
        </div>
    );
}

/**
 * The four numbers worth showing for the file that was just imported.
 *
 * 🔴 EVERY LABEL NAMES WHAT IT COUNTS. They used to read "Created", "Matched",
 * "Vehicles", "Services" — fixed, and none of them saying created WHAT. Importing 5,000
 * invoices showed "0 Created, 5000 Matched, 0 Vehicles, 0 Services", which reads as
 * though nothing happened: two of those cannot apply to an invoice file, and the two
 * that had numbers were counting the CUSTOMERS behind the invoices, not the five
 * thousand invoices, which went unmentioned. A bare verb makes the reader guess the
 * noun, and on this screen they guess wrong.
 *
 * Customers matched stays on every layout, because it is the reassurance that an
 * invoice or a quote found the right person instead of inventing one.
 */
/**
 * The server's own step after the last file: jobs raised for invoices still owed, and
 * quote jobs for imported quotes the customer never answered. No file, no slot — it is
 * queued by the server behind the last part and shows here like any other run.
 */
export const OPEN_WORK = 'OPEN_WORK';

/** What a run is called in the log: its file, or the step that has none. */
export function runTitle(run) {
    if (run.kind === OPEN_WORK) {
        return 'Open work';
    }
    return run.filename || run.kind;
}

export function statsFor(run) {
    if (run.kind === OPEN_WORK) {
        return [
            { label: 'Jobs raised from unpaid invoices', value: run.jobsRaised ?? 0 },
            { label: 'Quotes raised', value: run.quotesRaised ?? 0 },
        ];
    }
    if (run.invoicesCreated || run.invoicesMatched || run.lineItemsAdded) {
        return [
            { label: 'Invoices created', value: run.invoicesCreated },
            { label: 'Invoices already here', value: run.invoicesMatched },
            { label: 'Invoice lines', value: run.lineItemsAdded },
            { label: 'Customers matched', value: run.customersMatched },
        ];
    }
    if (run.quotesCreated || run.quotesMatched) {
        return [
            { label: 'Quotes created', value: run.quotesCreated },
            { label: 'Quotes already here', value: run.quotesMatched },
            { label: 'Quote lines', value: run.lineItemsAdded },
            { label: 'Customers matched', value: run.customersMatched },
        ];
    }
    if (run.paymentsApplied) {
        return [
            { label: 'Payments applied', value: run.paymentsApplied },
            { label: 'Customers created', value: run.customersCreated },
            { label: 'Customers matched', value: run.customersMatched },
            { label: 'Service records', value: run.serviceEntriesAdded },
        ];
    }
    if (run.workItemsAdded) {
        return [
            { label: 'Service records', value: run.serviceEntriesAdded },
            { label: 'Work items', value: run.workItemsAdded },
            { label: 'Customers matched', value: run.customersMatched },
            { label: 'Vehicles created', value: run.vehiclesCreated },
        ];
    }
    return [
        { label: 'Customers created', value: run.customersCreated },
        { label: 'Customers matched', value: run.customersMatched },
        { label: 'Vehicles created', value: run.vehiclesCreated },
        { label: 'Service records', value: run.serviceEntriesAdded },
    ];
}

/** What a finished run did, in numbers — the head of its expanded detail. */
function RunSummary({ run }) {
    return (
        <div className="rounded-xl border border-gray-200 p-4 space-y-3">
            <div className="flex items-center gap-2">
                <CheckCircle2 size={15} className="text-emerald-600" />
                <p className="text-sm font-semibold text-gray-900">Imported</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
                {statsFor(run).map((stat) => (
                    <Stat key={stat.label} label={stat.label} value={stat.value} />
                ))}
            </div>
            {run.rowsSkipped > 0 && (
                <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2.5">
                    {run.rowsSkipped} rows skipped — open the import below to see which, and why.
                </p>
            )}
            {run.jobsUndated > 0 && (
                <p className="flex items-start gap-1.5 text-xs text-gray-600">
                    <Info size={13} className="mt-0.5 shrink-0" />
                    {run.jobsUndated} jobs had not finished in Machanicdesk. They are dated
                    from when they were raised and marked, so they do not read as work that
                    was definitely completed.
                </p>
            )}
        </div>
    );
}

function Stat({ label, value }) {
    return (
        <div className="rounded-lg bg-gray-50 p-2">
            <p className="text-lg font-bold text-gray-900">{value}</p>
            <p className="text-[11px] text-gray-500">{label}</p>
        </div>
    );
}

/**
 * The skipped rows.
 *
 * ⚠️ `skipped` and `lowConfidence` are read defensively. A clean run has empty lists,
 * an empty collection is what a serialiser drops, and the key comes back absent — so
 * the healthy run is the one that would break this page.
 */
/**
 * One line saying what a run actually did.
 *
 * 🔑 Only the counts that mean something for THAT file, and every one of them names
 * what it counts. It used to print customers and service records for every run, so an
 * invoice import read "0 created · 0 matched · 0 service records" — a successful import
 * of thousands of invoices described as having done nothing. A count of zero is worth
 * printing when the file was about that thing and none happened; it is noise when the
 * file could never have produced one.
 */
export function summarise(run) {
    // 🔑 A run that has not finished has no counts to show, and printing its zeroes
    // would say "imported nothing" about work still in progress — the reading that
    // makes somebody upload the file a second time.
    if (run.status === 'QUEUED') {
        return `Queued · #${run.sequence ?? '?'} — waits for the file before it`;
    }
    if (run.status === 'RUNNING') {
        // Rows, when the server has counted them: the one number that says the run is
        // MOVING, which is what stops a person from starting it again.
        return run.rowsTotal > 0
            ? `Running — ${(run.rowsProcessed ?? 0).toLocaleString()} of `
              + `${run.rowsTotal.toLocaleString()} rows`
            : 'Running — this keeps going if you close the page';
    }
    if (run.status === 'FAILED') {
        return run.failureReason
            ? `Failed — ${run.failureReason}`
            : 'Failed. Nothing from this file was imported.';
    }
    if (run.status === 'SKIPPED') {
        // Not run at all, because a file it depends on failed. The reason names it.
        return run.failureReason
            ? `Skipped — ${run.failureReason}`
            : 'Skipped — a file this one depends on did not import.';
    }

    if (run.kind === OPEN_WORK) {
        // Both numbers, always — "0 quotes raised" is a fact about a workshop with no
        // unanswered quotes, not noise.
        return `${run.jobsRaised ?? 0} jobs raised from unpaid invoices · `
            + `${run.quotesRaised ?? 0} quotes raised`;
    }

    const parts = [];
    if (run.invoicesCreated || run.invoicesMatched) {
        parts.push(`${run.invoicesCreated} invoices created`);
        if (run.invoicesMatched) {
            parts.push(`${run.invoicesMatched} invoices already here`);
        }
    }
    if (run.quotesCreated || run.quotesMatched) {
        parts.push(`${run.quotesCreated} quotes created`);
        if (run.quotesMatched) parts.push(`${run.quotesMatched} quotes already here`);
    }
    if (run.lineItemsAdded) {
        parts.push(`${run.lineItemsAdded} ${run.quotesCreated || run.quotesMatched
            ? 'quote lines' : 'invoice lines'}`);
    }
    if (run.paymentsApplied) parts.push(`${run.paymentsApplied} payments applied`);
    if (run.customersCreated || run.customersMatched) {
        parts.push(`${run.customersCreated} customers created`,
            `${run.customersMatched} customers matched`);
    }
    if (run.vehiclesCreated) parts.push(`${run.vehiclesCreated} vehicles created`);
    if (run.serviceEntriesAdded) {
        parts.push(`${run.serviceEntriesAdded} service records`);
    }
    // The invoice lines that became work items on those records — the proof the jobs
    // came across with their work, not just their dates.
    if (run.workItemsAdded) parts.push(`${run.workItemsAdded} work items`);
    // A run that produced nothing at all still has to say so.
    return parts.length ? parts.join(' · ') : 'nothing imported';
}

function RunDetail({ run, detail }) {
    if (!detail) {
        return <p className="px-5 pb-4 text-xs text-gray-400">Loading…</p>;
    }
    const skipped = detail.skipped ?? [];
    const guessed = detail.lowConfidence ?? [];
    const shortened = detail.shortened ?? [];
    // ⚠️ Absent, not [], on a report stored before conflicts existed — and on every
    // clean run, because an empty list is omitted from the JSON entirely.
    const conflicts = detail.conflicts ?? [];

    // The numbers first, for a run that has them. A queued or running one has none
    // yet, and a failed one's zeroes would read as "imported nothing" about a file
    // that was never read.
    const summary = run?.status === 'COMPLETED' ? <RunSummary run={run} /> : null;

    if (skipped.length === 0 && guessed.length === 0 && shortened.length === 0
        && conflicts.length === 0) {
        return (
            <div className="px-5 pb-4 space-y-3">
                {summary}
                <p className="text-xs text-gray-500">Nothing was skipped.</p>
            </div>
        );
    }

    return (
        <div className="px-5 pb-4 space-y-4">
            {summary}
            {skipped.length > 0 && (
                <div>
                    <p className="text-xs font-semibold text-gray-700 mb-1.5">
                        Skipped rows ({skipped.length})
                    </p>
                    <div className="overflow-x-auto rounded-lg border border-gray-200">
                        <table className="min-w-full text-xs">
                            <tbody className="divide-y divide-gray-100">
                                {skipped.map((s, i) => (
                                    <tr key={`${s.sheet}-${s.rowNumber}-${i}`} className="align-top">
                                        <td className="px-2.5 py-2 whitespace-nowrap text-gray-400">
                                            {/* The open-work step has no file, so its
                                                skipped entries have no row: "row 0"
                                                would point at nothing. */}
                                            {s.rowNumber > 0 ? `${s.sheet} row ${s.rowNumber}` : s.sheet}
                                        </td>
                                        <td className="px-2.5 py-2 text-gray-700">{s.reason}</td>
                                        <td className="px-2.5 py-2 text-gray-500">
                                            {Object.entries(s.data ?? {})
                                                .filter(([, v]) => v)
                                                .map(([k, v]) => `${k}: ${v}`)
                                                .join(' · ')}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {conflicts.length > 0 && (
                <div>
                    <p className="text-xs font-semibold text-gray-700 mb-1.5">
                        Already used by someone else ({conflicts.length})
                    </p>
                    <p className="text-[11px] text-gray-500 mb-1.5">
                        These rows were imported, but the value shown was left empty because
                        another customer here already has it — an email address can only
                        belong to one customer. If they are the same person, merge them; if
                        they are not, give one of them a different address.
                    </p>
                    <div className="overflow-x-auto rounded-lg border border-gray-200">
                        <table className="min-w-full text-xs">
                            <tbody className="divide-y divide-gray-100">
                                {conflicts.map((c, i) => (
                                    <tr key={`${c.subject}-${c.field}-${i}`} className="align-top">
                                        <td className="px-2.5 py-2 whitespace-nowrap text-gray-400">
                                            {c.subject}
                                        </td>
                                        <td className="px-2.5 py-2 whitespace-nowrap text-gray-700">
                                            {c.value}
                                        </td>
                                        <td className="px-2.5 py-2 text-gray-500">
                                            already used by {c.heldBy}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {shortened.length > 0 && (
                <div>
                    <p className="text-xs font-semibold text-gray-700 mb-1.5">
                        Values that did not fit ({shortened.length})
                    </p>
                    <p className="text-[11px] text-gray-500 mb-1.5">
                        These rows were imported, but one of their values was longer than this
                        product stores and was shortened or left out. Worth a look if the field
                        matters.
                    </p>
                    <div className="overflow-x-auto rounded-lg border border-gray-200">
                        <table className="min-w-full text-xs">
                            <tbody className="divide-y divide-gray-100">
                                {shortened.map((v, i) => (
                                    <tr key={`${v.subject}-${v.field}-${i}`} className="align-top">
                                        <td className="px-2.5 py-2 whitespace-nowrap text-gray-400">
                                            {v.subject}
                                        </td>
                                        <td className="px-2.5 py-2 whitespace-nowrap text-gray-700">
                                            {v.field}
                                        </td>
                                        <td className="px-2.5 py-2 text-gray-500">
                                            {v.stored
                                                ? `${v.original} → ${v.stored}`
                                                : `${v.original} — not stored`}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {guessed.length > 0 && (
                <div>
                    <p className="text-xs font-semibold text-gray-700 mb-1.5">
                        Worked out from the vehicle description ({guessed.length})
                    </p>
                    <p className="text-[11px] text-gray-500 mb-1.5">
                        Machanicdesk exports no vehicle list, so make, model, colour and year are
                        read out of one line of text. These are the least certain.
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                        {guessed.map((g, i) => (
                            <span
                                key={`${g.rego}-${g.field}-${i}`}
                                className="text-[11px] px-2 py-1 rounded-full bg-gray-100 text-gray-600"
                            >
                                {g.rego} · {g.field}{g.value ? `: ${g.value}` : ' not found'}
                            </span>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
